import React, { useEffect, useState } from "react";
import { AutoComplete, Button, Card, message, Spin, Space } from "antd";
import { useParams } from "react-router-dom";
import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
  DroppableProvided,
  DraggableProvided,
} from "react-beautiful-dnd";

import { IBettingWebsite } from "../../../types/BettingWebsite";
import { getTop10ArticleByID, setTop10BettingWebsites } from "../action";
import { getBettingWebsites } from "../../BettingWebsites/action";

type Props = { fetchData: () => void };

const Top10BettingWebsitesSorter: React.FC<Props> = ({ fetchData }) => {
  const { id } = useParams();

  const [searchValue, setSearchValue] = useState("");
  const [options, setOptions] = useState<{ value: string; label: string }[]>(
    []
  );
  const [selected, setSelected] = useState<IBettingWebsite[]>([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchResults, setSearchResults] = useState<IBettingWebsite[]>([]);

  useEffect(() => {
    if (!id) return;
    const fetchArticle = async () => {
      try {
        setInitialLoading(true);
        const article = await getTop10ArticleByID(id);
        if (article?.Top10BettingWebsites) {
          setSelected(article.Top10BettingWebsites);
        }
      } catch (err: any) {
        message.error(err.message || "Failed to load article");
      } finally {
        setInitialLoading(false);
      }
    };
    fetchArticle();
  }, [id]);

  const handleSearch = async (value: string) => {
    setSearchValue(value);
    if (!value) {
      setOptions([]);
      return;
    }
    try {
      const results = await getBettingWebsites(10, 0, value);
      setSearchResults(results);
      setOptions(
        results.map((w) => ({
          value: w.id,
          label: w.MainInfo.Title.En || w.MainInfo.Title.Ar || "Untitled",
        }))
      );
    } catch (err: any) {
      message.error(err.message || "Failed to fetch websites");
    }
  };

  const handleSelect = (value: string) => {
    const website = searchResults.find((w) => w.id === value);
    if (website && !selected.some((s) => s.id === website.id)) {
      setSelected([...selected, website]);
    }
    setSearchValue("");
  };

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    const reordered = [...selected];
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);

    setSelected(reordered);
  };
  const handleRemove = (id: string) => {
    setSelected((prev) => prev.filter((w) => w.id !== id));
  };

  const handleSubmit = async () => {
    if (!id) {
      message.error("Missing article id");
      return;
    }
    setLoading(true);
    try {
      const ids = selected.map((w) => w.id);
      await setTop10BettingWebsites(id, ids);
      message.success("Top 10 websites updated successfully!");
      fetchData();
    } catch (err: any) {
      message.error(err.message || "Failed to update websites");
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: "center", padding: 40 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Card title="Sort Top 10 Betting Websites">
      <AutoComplete
        style={{ width: "100%", marginBottom: 16 }}
        options={options}
        onSearch={handleSearch}
        onSelect={handleSelect}
        placeholder="Search betting websites..."
        value={searchValue}
        onChange={setSearchValue}
      />

      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="bettingWebsites">
          {(provided: DroppableProvided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{
                minHeight: 100,
                border: "1px dashed #ccc",
                padding: 8,
                borderRadius: 6,
              }}
            >
              {selected.map((item, index) => (
                <Draggable key={item.id} draggableId={item.id} index={index}>
                  {(provided: DraggableProvided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        userSelect: "none",
                        padding: "8px 12px",
                        marginBottom: 8,
                        background: "#fafafa",
                        border: "1px solid #ddd",
                        borderRadius: 6,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        ...provided.draggableProps.style,
                      }}
                    >
                      <span>
                        {item.MainInfo.Title.En ||
                          item.MainInfo.Title.Ar ||
                          "Untitled"}
                      </span>
                      <Button
                        type="link"
                        danger
                        size="small"
                        onClick={() => handleRemove(item.id)}
                      >
                        Remove
                      </Button>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Button
        type="primary"
        onClick={handleSubmit}
        style={{ marginTop: 16 }}
        block
        disabled={selected.length === 0}
      >
        {loading ? <Spin /> : "Save Top 10"}
      </Button>
    </Card>
  );
};

export default Top10BettingWebsitesSorter;
